import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useImagingSettingsID, useSelectedRobotId } from 'redux/hooks/imagingSetup';
import { CapturePropertyRobotDTO, Robot, SaveProfileDTO } from 'business/models/Models';
import {
  disabledOption,
  externalConfig,
  FixedLightPathOptions,
  LightPathIds,
  lightPathIdsLookup,
  opticsFields,
} from '../constants';
import { useDispatch } from 'react-redux';
import { setSelectedRobotId } from 'redux/reducers/imagingSettingsReducer';
import { CaptureProfileType, FrapConfigType } from '../types';
import RobotSettingsForm from './RobotSettingsForm';
import HorizontalNavbar from 'components/customComponents/CommonComponents/HorizontalNavbar/HorizontalNavbar';

interface RobotSettingsProps {
  setIsDirty: (flag: boolean) => void;
  robots: Robot[];
  cpData: CaptureProfileType;
  setCpData: React.Dispatch<React.SetStateAction<CaptureProfileType>>;
}

function RobotSettings(props: RobotSettingsProps) {
  const { setIsDirty, robots, cpData, setCpData } = props;

  const [lightPath, setLightPath] = useState<string>();
  const [enabledFields, setEnabledFields] = useState<string[]>([]);
  const [robotConfig, setRobotConfig] = useState<string>();
  const [frapConfig, setFrapConfig] = useState<FrapConfigType>();
  const selectedCp = useImagingSettingsID();
  const robotId = useSelectedRobotId();

  const dispatch = useDispatch();

  const handleChange = (propertyName, value) => {
    setCpData((prevState) => {
      return {
        ...prevState,
        [selectedCp[0]]: new SaveProfileDTO({
          ...prevState[selectedCp[0]],
          capturePropertyRobotDTO: new CapturePropertyRobotDTO({
            ...prevState[selectedCp[0]]?.capturePropertyRobotDTO,
            data: {
              ...prevState[selectedCp[0]]?.capturePropertyRobotDTO?.data,
              [robotId]: {
                ...prevState[selectedCp[0]]?.capturePropertyRobotDTO?.data[robotId],
                [propertyName]: value,
              },
            },
          }),
        }),
      };
    });
    setIsDirty(true);
  };

  const robotCaptureProperties = useMemo(() => {
    return cpData[selectedCp[0]]?.capturePropertyRobotDTO;
  }, [cpData, selectedCp]);

  const handleLightPathSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value, options, selectedIndex } = e.target;
    setLightPath(options[selectedIndex].text);
    handleChange(name, value);
  };

  const handleFieldToggle = (propertyName: string, value?: any) => {
    if (!enabledFields.includes(propertyName)) {
      handleChange(propertyName, value);
      setEnabledFields([...enabledFields, propertyName]);
    } else {
      handleChange(propertyName, false);
      setEnabledFields(enabledFields.filter((item) => item !== propertyName));
    }
  };

  const handleRobotSelect = (e: any) => {
    dispatch(setSelectedRobotId(e.target.value));
  };

  const robotLightPaths = useMemo(() => {
    const config = robots?.find((r) => r.id === robotId)?.robotJson;
    const frapConfigData = robots?.find((r) => r.id === robotId)?.frapConfigJson;
    const lightPaths = [];
    if (config) {
      const parsedConfig = JSON.parse(config);
      setRobotConfig(parsedConfig);
      for (const key in parsedConfig) {
        if (parsedConfig[key] !== null && key !== externalConfig) {
          lightPaths.push(key);
        }
      }
    }
    if (frapConfigData) {
      const parsedJson = JSON.parse(frapConfigData);
      setFrapConfig(parsedJson);
    }
    return lightPaths;
  }, [robots, robotId]);

  const lightPathsOptions = useMemo(() => {
    const lightPaths =
      robotLightPaths?.map((l, _idx) => ({
        value: lightPathIdsLookup[l.toLowerCase()],
        label: l,
      })) || [];
    return [disabledOption, ...lightPaths];
  }, [robotLightPaths]);

  const renderRobots = useCallback(() => {
    return robots?.map((r) => (
      <li
        id={`robotsettings-robot-tab-${r.id}`}
        key={r.id}
        className={`px-2 py-1 w-48 truncate shrink-0 border cursor-pointer hover:bg-blue-100 ${
          robotId === r.id ? 'bg-blue-500/90 text-white hover:bg-blue-500/80' : ''
        }`}
        style={{ borderBottom: '2px solid red !important' }}
        value={r.id}
        title={r.name}
        onClick={handleRobotSelect}
      >
        {r.name}
      </li>
    ));
  }, [robots, robotId]);

  const getLightPathValue = () => {
    if (cpData[selectedCp[0]]?.name === 'Visible (Default)') {
      return LightPathIds.Visible;
    } else {
      return (
        robotCaptureProperties?.data?.[robotId]?.['LightPath'] || LightPathIds.Disabled
      );
    }
  };

  useEffect(() => {
    if (robotCaptureProperties?.data) {
      const fields = [];
      Object.entries(robotCaptureProperties.data[robotId] || {})?.map(([k, v]) => {
        if (v !== false) {
          fields.push(k);
        }
      });
      if (lightPathsOptions) {
        setLightPath(
          lightPathsOptions?.find(
            (x) =>
              x.value === parseInt(robotCaptureProperties.data?.[robotId]?.LightPath, 10),
          )?.label,
        );
      }
      setEnabledFields(fields);
    } else {
      setLightPath(FixedLightPathOptions.Disabled);
      setEnabledFields([]);
    }
  }, [robotCaptureProperties, lightPathsOptions, robotId]);

  useEffect(() => {
    if (robots?.length) {
      dispatch(setSelectedRobotId(robots[0].id));
    }
  }, [robots]);

  return selectedCp.length > 0 ? (
    <>
      <div
        id="robotsettings-title"
        className="m-0 p-2 font-bold text-lg w-full"
        style={{ borderBottom: '1px solid #dddddd' }}
      >
        Robot Settings
      </div>

      <HorizontalNavbar renderSelections={renderRobots} />

      {robots?.length ? (
        <div
          id="robotsettings-robots-wrapper"
          className="flex flex-col gap-4 w-full h-full p-2"
        >
          <div id="robotsettings-robots-lightpath" className="flex gap-2 w-2/5">
            <span
              id="robotsettings-robots-lightpathtype-title"
              className="text-lg font-bold"
            >
              Light Path Type
            </span>
            <select
              id="robotsettings-robots-lightpathtype-select"
              onChange={handleLightPathSelection}
              name="LightPath"
              className="text-lg min-w-[9rem]"
              disabled={
                !selectedCp.length || cpData[selectedCp[0]]?.name === 'Visible (Default)'
              }
              value={getLightPathValue()}
            >
              {lightPathsOptions?.map((c) => (
                <option
                  id={`robotsettings-robots-lightpathtype-option-${c.label}`}
                  key={c.value}
                  value={c.value}
                  label={c.label}
                >
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {robotConfig && robotConfig[lightPath]?.length ? (
            <RobotSettingsForm
              opticsFields={opticsFields}
              robotCaptureProperties={robotCaptureProperties}
              robotConfig={robotConfig}
              lightPath={lightPath}
              selectedCp={selectedCp}
              robotId={robotId}
              handleChange={handleChange}
              handleFieldToggle={handleFieldToggle}
              enabledFields={enabledFields}
              frapConfig={frapConfig}
            />
          ) : null}
        </div>
      ) : null}
    </>
  ) : (
    <></>
  );
}

export default RobotSettings;
